import { config } from "config";
import { UPDATE_PROJECT } from "../constants/storeActionTypes";
import { createSlice, PayloadAction, createAsyncThunk, isFulfilled } from '@reduxjs/toolkit';
import { selectedZone } from "constants/Timezones";
import jwtService from 'services/jwtService';
import { RootState } from 'store';

const defaultProject = {
  name: "",
  description: '',
  timezone: selectedZone,
  hdatum: 'wgs84',
  vdatum: 'wgs84',
  projection: 'merc',
  utmZone: 1,
  utmNorthing: "C",
  falseEasting: 0,
  falseNorthing: 0,
  latitudeOrigin: 0,
  longitudeMeridian: 0,
  scaleFactor: 0,
  firstStdParallel: 0,
  secondStdParallel: 0,
  amgZone: 48,
  mgaZone: 48,
  loZone: 15,
  voxelSize: 1.0,
  units: 'meter',
  originX: 0.0,
  originY: 0.0,
  originZ: 0.0,
  minX: -201326592,
  minY: -201326592,
  minZ: -49152,
  maxX: 201326592,
  maxY: 201326592,
  maxZ: 49152,
}


const projectFieldsMap = (formData: any | null) => {
    if (!formData) {
        return defaultProject;
    }

    const projection = formData.coord_projection;
    const data: any = {
        id: formData.ID,
        name: formData.name,
        description: formData.description,
        timezone: formData.timezone,
        hdatum: formData.coord_hdatum,
        vdatum: formData.coord_vdatum,
        projection,
        voxelSize: formData.voxel_size,
        originX: formData.coord_origin_x,
        originY: formData.coord_origin_y,
        originZ: formData.coord_origin_z,
        units: formData.coord_unit,
    };
    
    if (projection === "utm") {
        data.utmNorthing = formData.coord_projection_utm_northing;
        data.utmZone = formData.coord_projection_utm_easting;
    } else if (projection === "tm") {
        data.falseEasting = formData.coord_projection_tm_falseEasting;
        data.falseNorthing = formData.coord_projection_tm_falseNorthing;
        data.latitudeOrigin = formData.coord_projection_tm_latOriginDeg;
        data.longitudeMeridian = formData.coord_projection_tm_longMeridianDeg;
        data.scaleFactor = formData.coord_projection_tm_scaleFactor;
    } else if (projection === "lcc") {
        data.falseEasting = formData.coord_projection_lcc_falseEasting;
        data.falseNorthing = formData.coord_projection_lcc_falseNorthing;
        data.latitudeOrigin = formData.coord_projection_lcc_latOfOriginDeg;
        data.longitudeMeridian = formData.coord_projection_lcc_longOfOriginDeg;
        data.firstStdParallel = formData.coord_projection_lcc_firstStdParallelDeg;
        data.secondStdParallel = formData.coord_projection_lcc_secondStdParallelDeg;
    } else if (projection === "aeac") {
        data.falseEasting = formData.coord_projection_aeac_falseEasting;
        data.falseNorthing = formData.coord_projection_aeac_falseNorthing;
        data.latitudeOrigin = formData.coord_projection_aeac_latOfOriginDeg;
        data.longitudeMeridian = formData.coord_projection_aeac_longOfOriginDeg;
        data.firstStdParallel = formData.coord_projection_aeac_firstStdParallelDeg;
        data.secondStdParallel = formData.coord_projection_aeac_secondStdParallelDeg;
    } else if (projection === "amg") {
        data.amgZone = formData.coord_projection_amg_zone;
    } else if (projection === "mga") {
        data.mgaZone = formData.coord_projection_mga_zone;
    } else if (projection === "lo") {
        data.loZone = formData.coord_projection_lo_zone;
    }

    return data;
}

interface IProjectState {
    id: string,
    path: string,
    data: any,
    dialogBox: {
        type: string,
        props: {
            open: boolean
        },
        data: any | null
    },
    success: boolean,
	loading: boolean,
	error: any | null,
	requestDone: boolean,
	lastRequest: string | null,
	currentRequestId: any,
	updated: number | null,
    lastCreated: any | null
}
const initialState = {
    id: '',
    path: 'home',
    data: {},
    dialogBox: {
        type: 'new',
        props: {
            open: false
        },
        data: null
    },
    success: false,
	loading: false,
	error: null,
	requestDone: false,
	lastRequest: '',
	currentRequestId: undefined,
	updated: 0,
    lastCreated: null
} satisfies IProjectState as IProjectState;

export const remoteCreateProject = createAsyncThunk('projects/new', async (project: any, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const owner_email =  state.auth.data.owner_email;

	try {
        const formData = new FormData();
        formData.append('operation', 'create');
        formData.append('data', JSON.stringify(project));
		const response = await jwtService.fetchRequest({
			method: 'POST',
			url: `/entity.ashx`,
			data: formData
		});
		const data = response.data;

        if(owner_email && data.result === 'success'){
            const newProjectId = data.id;
            //Assign the project newly created to the adminuser
            try{
                const endpoint = process.env.REACT_APP_BASE_URL + '/vf/update-project-level';
                let response = await jwtService.fetchRequest({
                    method: 'POST',
                    url: endpoint,
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    data: {
                        email : owner_email, 
                        projectId : newProjectId, 
                        access_level : 'full',
                        key_id : process.env.REACT_APP_BASE_APIKEY
                    }
                });

            }catch(e){
                console.log(e)
            }
        }
        
		return data;

	} catch (err) {
		return rejectWithValue(err);
	}
});

export const remoteUpdateProject = createAsyncThunk('projects/update', async (project: any, { getState, rejectWithValue }) => {
	const state = getState() as RootState;
    const { id } = state.projects;

	try {
		const response = await jwtService.fetchRequest({
			method: 'POST',
			url: `/entity.ashx?id=${id}&project=${id}`,
			data: project,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
		});
		const data = response.data;

		return data;
	} catch (err) {
		return rejectWithValue(err);
	}
});


type DeletePayloadArg = {
    projectId: string;
    itemId: string;
};



export const remoteDeleteProject = createAsyncThunk('projects/delete', async (args: DeletePayloadArg, { getState, rejectWithValue }) => {
    
    const { projectId, itemId } = args;
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const apiKey = process.env.REACT_APP_BASE_APIKEY;

    if (!baseUrl || !apiKey) {
      return rejectWithValue('Missing API config.');
    }
    
   	try {
    
        const email = atob(projectId)
        let response = await jwtService.fetchRequest({
            method: 'POST',
            url: `${baseUrl}/vf/remove-user-project`,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            data: {email : email, projectId : itemId, key_id : apiKey}
        });

        if (!response.data?.success) return rejectWithValue(response.data?.error || 'Unknown error');
        
      	return response.data;
        
	} catch (err) {
		return rejectWithValue((err as Error).message);
	}
});

const projectsSlice = createSlice({
	name: 'projects',
	initialState,
	reducers: {
		setProjectId: (state: any, action: PayloadAction<any>) => {
            return {
                ...state,
                id:  action.payload
            };
		},
		setPath: (state: any, action: PayloadAction<any>) => {
            return {
                ...state,
                path:  action.payload
            };
		},
		setProjectData: (state: any, action: PayloadAction<any>) => {
            return {
                ...state,
                datas:  {
                    ...state.datas,
                    ...action.payload
                }
            };
		},
		updateProject: (state: any = initialState, action: PayloadAction<any>) => {
			const { id, datas } = action.payload
            return {
                ...state,
                id,
                datas
            };
		},
		resetProject: (state) => {
            return {
                ...initialState,
            };
		},
		openNewDialog: (state: any = initialState, action: PayloadAction<any>) => {
            return {
                ...state,
                dialogBox: {
                    type: 'new',
                    props: {
                        open: true
                    },
                    data: {
                        ...defaultProject,
                        ...(action?.payload || {})
                    }
                }
            };
		},
		closeNewDialog: (state: any = initialState) => {
            return {
                ...state,
                dialogBox: {
                    type: 'new',
                    props: {
                        open: false
                    },
                    data: null
                }
            };
		},
		openHomeNewProjectDialog: (state: any = initialState, action: PayloadAction<any>) => {
            return {
                ...state,
                dialogBox: {
                    type: 'homenewproject',
                    props: {
                        open: false
                    },
                    data: null
                }
            };
		},
		openHomeNewViewDialog: (state: any = initialState, action: PayloadAction<any>) => {
            return {
                ...state,
                dialogBox: {
                    type: 'homenewview',
                    props: {
                        open: false
                    },
                    data: null
                }
            };
		},
		openEditDialog: (state: any = initialState, action: PayloadAction<any>) => {
            const data = projectFieldsMap(action?.payload || null);
            return {
                ...state,
                id: data.id || '',
                data: {
                    ...data
                },
                dialogBox: {
                    type: 'edit',
                    props: {
                        open: true
                    },
                    data: {
                        ...data
                    }
                }
            };
		},
		closeEditDialog: (state: any = initialState) => {
            return {
                ...state,
                dialogBox: {
                    type: 'edit',
                    props: {
                        open: false
                    },
                    data: null
                }
            };
		},
        updateDialogData: (state: any = initialState, action: PayloadAction<any>) => {
            return {
                ...state,
                dialogBox: {
                    ...state.dialogBox,
                    data: {
                        ...state.dialogBox.data,
                        ...(action?.payload || {})
                    }
                }
            };
		},
		removeRequestDone: (state) => {
			return {
				...state,
				requestDone: false
			}
			
		},
		loadingStatus: (state, action) => {
			return {
				...state,
				loading: action.payload
			}
			
		},
		resetLastRequest: (state) => {
			return {
				...state,
				lastRequest: null
			}
		}
	},
	extraReducers: (builder) => {
        builder.addCase(remoteCreateProject.pending, (state, action) => {
			if (state.loading) return;
            state.loading = true;
            state.requestDone = false;
            state.success = false;
			state.currentRequestId = action.meta.requestId;
            state.error = null;
            state.lastRequest = 'create';
		})
        builder.addCase(remoteCreateProject.fulfilled, (state, action) => {
			const { requestId } = action.meta;
            if (!(state.loading && state.currentRequestId === requestId)) {
				return;
			}

            const isSuccess = action.payload.result === 'success';
			state.currentRequestId = undefined;
			action.payload.result === 'error' && (state.error = action.payload.error_info || 'Unknown error');
			state.success = isSuccess;
			state.loading = false;
			state.requestDone = true;
			if (action.payload && isSuccess) {
				state.lastCreated = {
					id: action.payload.id,
				};
			}
			state.updated = Date.now();
		})
        builder.addCase(remoteCreateProject.rejected, (state, action) => {
			const { requestId } = action.meta;
         
			if (!(state.loading && state.currentRequestId === requestId)) return;
            state.error = action.payload ? action.payload : action?.error;
            state.loading = false;
            state.requestDone = true;
            state.currentRequestId = undefined;
		})
        builder.addCase(remoteUpdateProject.pending, (state, action) => {
			if (state.loading) return;
            state.loading = true;
            state.requestDone = false;
            state.success = false;
			state.currentRequestId = action.meta.requestId;
            state.error = null;
            state.lastRequest = 'update';
		})
        builder.addCase(remoteUpdateProject.fulfilled, (state, action) => {
			const { requestId } = action.meta;
            if (!(state.loading && state.currentRequestId === requestId)) {
				return;
			}

			state.currentRequestId = undefined;
			action.payload.result === 'error' && (state.error = action.payload.error_info || 'Unknown error');
			state.success = !action.payload.error;
			state.loading = false;
			state.requestDone = true;
			/*if (action.payload && !action.payload.error) {
				state.data = {
					...state.data,
					...action.payload
				};
			}*/
			state.updated = Date.now();
		})
        builder.addCase(remoteUpdateProject.rejected, (state, action) => {
			const { requestId } = action.meta;
         
			if (!(state.loading && state.currentRequestId === requestId)) return;
            state.error = action.payload ? action.payload : action?.error;
            state.loading = false;
            state.requestDone = true;
            state.currentRequestId = undefined;
		})
        builder.addCase(remoteDeleteProject.pending, (state, action) => {
			if (state.loading) return;
            state.loading = true;
            state.requestDone = false;
            state.success = false;
			state.currentRequestId = action.meta.requestId;
            state.error = null;
            state.lastRequest = 'delete';
		})
        builder.addCase(remoteDeleteProject.fulfilled, (state, action) => {
			const { requestId } = action.meta;
            if (!(state.loading && state.currentRequestId === requestId)) {
				return;
			}

			state.currentRequestId = undefined;
			action.payload.result === 'error' && (state.error = action.payload.error_info || 'Unknown error');
			state.success = !action.payload.error;
			state.loading = false;
			state.requestDone = true;
			state.updated = Date.now();
		})
        builder.addCase(remoteDeleteProject.rejected, (state, action) => {
			const { requestId } = action.meta;
         
			if (!(state.loading && state.currentRequestId === requestId)) return;
            state.error = action.payload ? action.payload : action?.error;
            state.loading = false;
            state.requestDone = true;
            state.currentRequestId = undefined;
		})
	}
});

export const { 
	setProjectId,
    setPath,
	setProjectData,
	updateProject,
	resetProject,
    openNewDialog,
	closeNewDialog,
	openEditDialog,
	closeEditDialog,
    updateDialogData,
    loadingStatus,
	removeRequestDone,
	resetLastRequest,
    openHomeNewProjectDialog,
    openHomeNewViewDialog
 } = projectsSlice.actions;

export type ProjectState = ReturnType<typeof projectsSlice.reducer>;
export default projectsSlice.reducer;
